import styled, { createGlobalStyle } from 'styled-components';
import { Button, Layout, Select, Input, DatePicker, Space, Table } from 'antd';
import CustomSelect from 'components/CustomSelect';
import { IToken, IThemeProps } from 'store/ducks/globalTypes';
import { IThemeSelectorProps } from 'store/ducks/Global/types';

const { RangePicker } = DatePicker;
const { Header } = Layout;

export const GlobalThemeStyles = createGlobalStyle<{ scheme?: string }>`
  /* Apply theme-based table header colors */
  ${(props) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[props.scheme || 'deepBlueTheme'];
    const headerColor = currentTheme?.mainColor || '#000000';

    return `
      .ant-table-thead > tr > th,
      .ant-table-thead th {
        color: ${headerColor} !important;
      }
    `;
  }}
  
  ${(props) =>
    (props.scheme === 'lightUITheme' || props.scheme === 'pinkUITheme') &&
    `
    .ant-table-thead th {
      color: #000 !important;
    }
  `}
  
  ${(props) =>
    props.scheme === 'greenModeTheme' &&
    `
    .ant-table-cell {
      background: #fff !important;
      background-color: #fff !important;
    }
    
    .ant-table-tbody > tr > td {
      background: #fff !important;
      background-color: #fff !important;
    }
    
    .ant-modal-content {
      background: #fff !important;
      background-color: #fff !important;
    }
    
    .ant-modal-body {
      background: #fff !important;
      background-color: #fff !important;
    }
  `}
  
  ${(props) =>
    props.scheme === 'deepNavyTheme' &&
    `
    .ant-btn:not(:disabled) {
      filter: brightness(1.5) !important;
    }
    
    .ant-btn:not(:disabled).save-button-deepnavy {
      filter: brightness(1.8) !important;
    }
  `}
  
  ${(props) =>
    (props.scheme === 'darkUITheme' || props.scheme === 'deepNavyTheme') &&
    `
    /* Dark theme table text styling */
    .ant-table-thead > tr > th {
      color: #ffffff !important;
    }
    
    .ant-table-tbody > tr > td {
      color: #ffffff !important;
    }
    
    .ant-modal-title {
      color: #ffffff !important;
    }
  `}
`;

export const StyledLayout = styled(Layout)<IToken & IThemeProps>`
  min-height: 100vh;

  > .ant-layout {
    min-height: initial;

    .ant-layout-header {
      height: fit-content;
    }
  }

  .ant-table-content {
    margin-left: -10px;
  }

  .ant-table-thead tr .ant-table-cell {
    padding-left: 8px;
  }

  ${(props) =>
    (props.scheme === 'lightUITheme' || props.scheme === 'pinkUITheme') &&
    `
    && .ant-table-thead > tr > th,
    && .ant-table-thead th,
    && .ant-table-cell {
      color: #000 !important;
    }
  `}

  aside {
    // padding-right: ${(props) => `${props.token.paddingLG}px`};
    flex: initial !important;
    text-align: center;
    max-width: fit-content !important;
    min-width: fit-content !important;
    width: fit-content !important;
    position: relative !important;
    right: 9px;
    transition: transform 0.3s ease-in-out;
  }
`;

export const StyledHeader = styled(Header)<IToken>`
  display: flex;
  flex-direction: row-reverse;
  gap: 20px;
  padding-inline: ${(props) => `${props.token.paddingLG}px`};
  line-height: ${(props) => `${props.token.lineHeightHeading2}px`};
  flex-wrap: wrap;
  position: relative;
  z-index: 1200;
  transform: translateX(-100px);

  @media (max-width: 1200px) {
    gap: 12px;
    padding-inline: 16px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
    padding: 12px 8px;
  }
`;

export const StyledButton = styled(Button)<{
  isInternalControlPage?: boolean;
  scheme?: string;
}>`
  width: 110px;
  text-transform: uppercase;
  align-self: end;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: fit-content;
  margin-right: ${(props) => (props.isInternalControlPage ? '45px' : '0px')};
  margin-top: ${(props) => (props.isInternalControlPage ? '10px' : '0px')};
  margin-left: -30px;
  position: relative;
  z-index: 1000;

  &.display-save-button {
    min-width: 100px;
    height: 25px;
    position: relative;
    z-index: 1000;
    margin: 0;
    width: auto;

    ${(props) =>
      props.scheme === 'deepNavyTheme' &&
      !props.disabled &&
      props.children === 'Save' &&
      `
      filter: brightness(1.8) !important;
    `}
  }
`;

export const FilterContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 23px;
  gap: 8px;
`;

export const StyledLogo = styled.img.attrs({
  src: require('../../assets/img/Auriscom logo.png'),
  alt: 'Auriscom Logo'
})`
  position: absolute;
  top: 20px;
  left: -50px;
  width: 200px;
  height: auto;
  object-fit: contain;
`;

export const HorizontalLayout = styled('div')<IThemeSelectorProps>`
  padding: 0 24px 24px;
`;

export const OpenCloseButton = styled(Button)`
  width: 30px;
  text-transform: uppercase;
  align-self: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: fit-content;
  position: absolute;
  top: -6px;
  left: -40px;
  z-index: 2000;
  padding: 5px 0;
`;

export const Container = styled('div')`
  padding: 0px;
`;

export const GridContainer = styled('div')<{ token: any }>`
  display: grid;
  grid-template-columns: repeat(3, 300px);
  gap: 32px;
  justify-content: center;
  width: 100%;

  > a {
    width: 300px;
  }

  .ant-card {
    width: 300px;
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 300px);
    gap: 24px;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 16px;

    > a,
    .ant-card {
      width: 100%;
      max-width: 320px;
      margin: 0 auto;
    }
  }

  @media (max-width: 480px) {
    gap: 14px;

    > a,
    .ant-card {
      max-width: 260px;
      margin: 0 auto;
    }
  }

  @media (max-width: 380px) {
    gap: 10px;

    > a,
    .ant-card {
      max-width: 220px;
      margin: 0 auto;
    }
  }
`;

export const StepsContainer = styled('div')<IThemeProps>`
  background: ${(props) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[props.scheme || 'mainTheme'];
    return currentTheme?.colorBgLayout || themesList.mainTheme.colorBgLayout;
  }};
  padding: 10px 24px;
  border-radius: 0px;
  margin-bottom: 5px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: center;
  transition: all 0.3s ease;

  .ant-steps {
    width: 100%;
  }

  .ant-steps-item-container {
    cursor: pointer;
    padding: 0 8px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  /* Square step icons with theme colors */
  .ant-steps-item-icon {
    width: 28px !important;
    height: 28px !important;
    line-height: 28px !important;
    font-size: 14px !important;
    border-radius: 4px;
    transition: all 0.3s ease;

    /* Default styling */
    background-color: #f5f5f5;
    border: 1px solid #d9d9d9;
  }

  /* Apply theme-specific colors to all step states */
  ${(props) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[props.scheme || 'mainTheme'];
    const primaryColor = currentTheme?.colorPrimary || '#1890ff';
    const hoverColor = currentTheme?.colorPrimaryBgHover || '#40a9ff';

    return `
      /* Process (current) step */
      .ant-steps-item-process .ant-steps-item-icon {
        background-color: ${primaryColor} !important;
        border-color: ${primaryColor} !important;

        .ant-steps-icon {
          color: #fff !important;
          font-weight: bold !important;
        }
      }
      
      /* Finished steps */
      .ant-steps-item-finish .ant-steps-item-icon {
        background-color: ${primaryColor} !important;
        border-color: ${primaryColor} !important;
        
        .ant-steps-icon {
          color: #fff !important;
          font-weight: bold !important;
        }
      }
      
      /* Waiting steps */
      .ant-steps-item-wait .ant-steps-item-icon {
        background-color: #f5f5f5 !important;
        border-color: ${primaryColor} !important;
        
        .ant-steps-icon {
          color: ${primaryColor} !important;
          font-weight: bold !important;
        }
      }
      
      /* Hover effects */
      .ant-steps-item-container:hover .ant-steps-item-icon {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }
    `;
  }}

  .ant-steps-item-title {
    font-size: 14px !important;
    font-weight: 500;
    color: #333 !important;
  }

  .ant-steps-item-title::after {
    display: none !important;
  }

  .ant-steps-item-description {
    font-size: 12px !important;
    color: #666;
  }

  /* Special handling for specific themes that need different text colors */
  ${(props) =>
    props.scheme === 'eighthTheme' &&
    `
    .ant-steps-item-title {
      color: #fff !important;
    }
  `}

  ${(props) =>
    props.scheme === 'darkUITheme' &&
    `
    .ant-steps-item-title {
      color: #fff !important;
    }
  `}
`;

export const HorizontalFilterBar = styled('div')<IToken & IThemeProps>`
  background: ${(props) => {
    const { themesList } = require('constants/themesList');
    const currentTheme = themesList[props.scheme || 'mainTheme'];
    return currentTheme?.colorBgLayout || themesList.mainTheme.colorBgLayout;
  }};
  padding: 12px 16px;
  padding-left: 35px;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-height: 60px;
  position: relative;
  z-index: 900;
  clear: both;
  justify-content: space-between;

  @media (max-width: 1500px) {
    flex-wrap: nowrap;
    gap: 1px;

    * {
      .ant-select {
        min-width: 120px !important;
        width: 120px !important;
        max-width: 120px !important;

        .ant-select-selector {
          width: 120px !important;
          min-width: 120px !important;
          max-width: 120px !important;
        }
      }
    }
  }

  @media (max-width: 1105px) and (min-width: 870px) {
    gap: 8px;
    padding: 10px 12px;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 8px;
    gap: 12px;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
  }
`;

export const FilterItem = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;

  @media (max-width: 1500px) {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
    gap: 1px;
    margin-right: 12px;
  }

  @media (max-width: 1105px) and (min-width: 870px) {
    min-width: 140px;
    flex: 1;
  }

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }

  @media (max-width: 480px) {
    min-width: 100%;
    width: 100%;
  }
`;

export const FilterLabel = styled('label')`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme?.token?.colorTextSecondary};
  margin-bottom: 4px;
`;

export const StyledSelect = styled(Select)`
  min-width: 600px !important;
  width: 600px !important;
  max-width: 600px !important;

  .ant-select-selector {
    border-radius: 6px;
    height: 65px !important;
    width: 600px !important;
    font-size: 16px !important;
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 6px;
`;

export const StyledRangePicker = styled(RangePicker)`
  border-radius: 6px;
`;

export const ActionButtons = styled('div')<{
  isClientReqPage?: boolean;
  hasDisplayedData?: boolean;
  isDataManagementPage?: boolean;
  isCompliancePage?: boolean;
  isComplianceCreationPage?: boolean;
  isAuditFindingsPage?: boolean;
  isWhoAmIPage?: boolean;
}>`
  display: flex;
  gap: 8px;
  // margin-top: ${(props) => (props.isWhoAmIPage ? '23px' : '25px')};
  flex-wrap: nowrap;
  align-items: ${(props) => (props.isWhoAmIPage ? 'center' : 'initial')};
  margin-left: 23px;

  @media (max-width: 1500px) {
    gap: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  @media (max-width: 1105px) and (min-width: 870px) {
    gap: 6px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: nowrap;
  }

  @media (max-width: 480px) {
    margin-left: 0;
    justify-content: flex-start;
    flex-direction: row;
    gap: 8px;
  }
`;

export const ApplyButton = styled(Button)<
  IThemeProps &
    Partial<IToken> & {
      isClientReqPage?: boolean;
      hasDisplayedData?: boolean;
      isCompliancePage?: boolean;
      isComplianceCreationPage?: boolean;
    }
>`
  border-radius: 6px;
  font-weight: 500;

  ${(props) =>
    props.scheme === 'deepNavyTheme' &&
    !props.disabled &&
    props.children === 'Save' &&
    `
    && {
      filter: brightness(1.8) !important;
    }
  `}

  background-color: ${(props) => {
    if (props.type === 'primary') {
      return '#ff7875';
    }
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#067de4ff';
    }
    // Other buttons only change in eighthTheme
    return props.scheme === 'eighthTheme' ? '#238636 !important' : 'inherit';
  }};
  border-color: ${(props) => {
    if (props.type === 'primary') {
      return '#ff7875';
    }
    // Copy All button in darkUITheme uses blue border
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#44a7fb';
    }
    return props.scheme === 'eighthTheme' ? '#238636 !important' : 'inherit';
  }};
  color: ${(props) => {
    if (props.type === 'primary') {
      return '#fff !important';
    }
    // Copy All button in darkUITheme uses white text
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#fff !important';
    }
    // Copy All button in deepNavyTheme uses white text
    if (props.scheme === 'deepNavyTheme' && props.children === 'Copy All') {
      return '#fff !important';
    }
    // Merge selected, Copy selected, Save buttons in deepNavyTheme use black text
    if (
      props.scheme === 'deepNavyTheme' &&
      (props.children === 'Merge selected' ||
        props.children === 'Copy selected' ||
        props.children === 'Save')
    ) {
      return '#000000 !important';
    }
    return props.scheme === 'eighthTheme' ? '#fff !important' : 'inherit';
  }};

  @media (max-width: 1500px) {
    width: auto !important;
    min-width: 60px;
    font-size: ${(props) => {
      if (props.isClientReqPage && props.hasDisplayedData) return '10px';
      return '12px';
    }};
    padding: 4px 8px;
    white-space: normal;
    word-break: break-word;
    height: auto;
    line-height: 1.3;
    text-align: center;
    margin-left: ${(props) => {
      if (props.type === 'primary' && props.children === 'Display') {
        if (
          !props.hasDisplayedData &&
          (props.isCompliancePage || props.isComplianceCreationPage)
        )
          return '150px';
        return '-20px';
      }
      return '0';
    }};
  }

  @media (max-width: 768px) {
    margin-left: 0 !important;
    width: auto !important;
  }

  &:hover {
    background-color: ${(props) => {
      if (props.type === 'primary') {
        return '#ff9c99';
      }
      // Copy All button in darkUITheme uses same hover color as Display button
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#ff9c99';
      }
      // Merge, Copy, Save buttons in darkUITheme use blue hover
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#44a7fb';
      }
      return props.scheme === 'eighthTheme' ? '#1e7a2e !important' : 'inherit';
    }};
    border-color: ${(props) => {
      if (props.type === 'primary') {
        return '#ff9c99';
      }
      // Copy All button in darkUITheme uses blue hover border
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#44a7fb';
      }
      // Merge, Copy, Save buttons in darkUITheme use blue hover border
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#44a7fb';
      }
      return props.scheme === 'eighthTheme' ? '#1e7a2e !important' : 'inherit';
    }};
    color: ${(props) => {
      if (props.type === 'primary') {
        return '#fff !important';
      }
      // Copy All button in darkUITheme uses white text on hover
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#fff !important';
      }
      // Merge, Copy, Save buttons in darkUITheme use white text on hover
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#fff !important';
      }
      // Merge selected, Copy selected, Save buttons in deepNavyTheme use white text on hover
      if (
        props.scheme === 'deepNavyTheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#fff !important';
      }
      return props.scheme === 'eighthTheme' ? '#fff !important' : 'inherit';
    }};
  }
`;

export const ButtonContainer = styled('div')`
  display: flex;
  gap: 12px;
  padding: 8px 24px;
  margin-bottom: 8px;
`;

export const StyledCustomSelect = styled(CustomSelect)`
  min-width: 160px;
  border-radius: 6px;

  .ant-select-selector {
    width: 160px !important;
  }

  .ant-select-arrow {
    right: 8px !important;
    left: auto !important;
  }

  .ant-select-selection-placeholder {
    padding-left: 8px !important;
  }

  .ant-select-selection-item {
    padding-left: 8px !important;
  }

  @media (max-width: 1500px) {
    min-width: 80px !important;
    width: 80px !important;

    .ant-select-selector {
      width: 80px !important;
    }
  }

  @media (max-width: 1105px) and (min-width: 870px) {
    min-width: 140px;

    .ant-select-selector {
      width: 140px !important;
    }
  }

  @media (max-width: 768px) {
    min-width: 120px;
    flex: 1;

    .ant-select-selector {
      width: 100% !important;
    }
  }

  @media (max-width: 480px) {
    min-width: 100%;

    .ant-select-selector {
      width: 100% !important;
    }
  }
`;

export const StyledSelectWithArrow = styled(Select)`
  min-width: 150px;
  border-radius: 6px;

  @media (max-width: 1500px) {
    min-width: 80px !important;
    width: 80px !important;

    .ant-select-selector {
      width: 80px !important;
    }
  }
`;

export const FilteredContainerRisks = styled('div')<{
  isBusinessProcesses?: boolean;
}>`
  display: flex;
  align-items: anchor-center;
  gap: 16px;
  margin-left: ${(props) => (props.isBusinessProcesses ? '-40px' : '0')};
  max-width: 80%;
`;

export const Spacer = styled('div')`
  width: 16px;
`;

export const NavigationContainer = styled('div')<{ isProcessL4?: boolean }>`
  margin-top: ${(props) => (props.isProcessL4 ? '10px' : '2px')};
  width: ${(props) => (props.isProcessL4 ? '100%' : 'auto')};
`;

export const NavigationSpace = styled(Space)<{ isProcessL4?: boolean }>`
  margin-top: ${(props) => (props.isProcessL4 ? '-10px' : '-11px')};
  margin-left: 20px;
`;

export const NavigationButton = styled(Button)<IToken & IThemeProps>`
  color: ${(props) => {
    if (props.scheme === 'deepNavyTheme' || props.scheme === 'darkUITheme') {
      return props.token?.colorTextLightSolid;
    }
    return props.token?.colorText;
  }};
`;

export const RelativeContainer = styled('div')`
  position: relative;
`;

export const StyledTable = styled(Table)`
  margin-top: 16px;
`;

export const StyledHorizontalFilterBar = styled(HorizontalFilterBar)`
  margin-left: 5px;
  width: calc(100% - 70px);
`;

export const HorizontalLayoutMain = styled('div')<IThemeSelectorProps>`
  @media (max-width: 1400px) {
    padding: 24px;
  }
  .ant-tabs-nav-list {
    width: -webkit-fill-available;
    margin-bottom: 1rem;

    .ant-tabs-tab {
      width: -webkit-fill-available;
      display: table;
    }
  }
`;

export const CardItemWrapper = styled('div')`
  position: relative;

  .ant-card-meta-detail {
    padding: 10px;
  }
`;

export const ContainerDependency = styled('div')<{ token: any }>`
  justify-items: center;
  width: 100%;

  .ant-tree {
    background: unset;

    .ant-tree-treenode {
      margin-top: 15px;
    }

    .ant-tree-node-content-wrapper {
      padding-inline: 35px;
    }
  }

  > a {
    width: 300px;
  }

  .ant-card {
    width: 300px;
  }
`;
